/**
 * Per-device preference for whether the floating AI assistant is shown.
 * Stored in localStorage so the choice persists, with a custom event so every
 * mounted component (bubble + profile toggle) stays in sync instantly.
 */
import { useCallback, useEffect, useState } from 'react';

const KEY = 'db-ai-assist-enabled';
const EVT = 'db-ai-assist-changed';
const OPEN_EVT = 'db-ai-assist-open';

/**
 * Open the AI assistant panel from anywhere (e.g. a menu link), even when the
 * floating bubble is turned off for this device.
 */
export function openAiAssistant() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OPEN_EVT));
  }
}

export const AI_OPEN_EVENT = OPEN_EVT;

function read(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(KEY) !== 'off';
}

export function useAiAssist() {
  const [enabled, setEnabledState] = useState<boolean>(read);

  useEffect(() => {
    const sync = () => setEnabledState(read());
    window.addEventListener(EVT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(KEY, v ? 'on' : 'off');
      window.dispatchEvent(new Event(EVT));
    }
    setEnabledState(v);
  }, []);

  return { enabled, setEnabled };
}
